import DB_PATH from "../DB_PATH";
import { firestore } from "../firebase";
import { Roundtable } from "../../types/Roundtable";
import { generateRoundtableAgenda } from "./generateRoundtableAgenda";
import { mapRoundtableFormValues, RoundtableFormValues } from "./mapRoundtableForm";

// Firestore rejects undefined, so drop keys left out (recurrence on a single
// roundtable, maxParticipants when no cap, creatorName when unknown).
const stripUndefined = <T extends Record<string, unknown>>(value: T): T => {
  const next: Record<string, unknown> = {};
  Object.keys(value).forEach((key) => {
    if (value[key] !== undefined) next[key] = value[key];
  });
  return next as T;
};

type CreateRoundtableParams = {
  values: RoundtableFormValues;
  communityId: string;
  createdBy: string;
  creatorName?: string;
};

// Builds the full roundtable doc the app expects: maps the form fields, asks the
// callable for an author-led agenda, and writes to CommunityRoundtable. A series
// template (recurrence active) carries no attendees; the app projects/materializes
// its occurrences.
export default async ({
  values,
  communityId,
  createdBy,
  creatorName,
}: CreateRoundtableParams): Promise<Roundtable> => {
  const mapped = mapRoundtableFormValues(values);

  const agenda = await generateRoundtableAgenda({
    title: mapped.title,
    goal: mapped.goal,
    communityId,
  });

  const ref = firestore.collection(DB_PATH.COMMUNITY_ROUNDTABLE).doc();
  const nowIso = new Date().toISOString();

  const payload: Roundtable = stripUndefined({
    id: ref.id,
    communityId,
    title: mapped.title,
    goal: mapped.goal,
    startTime: mapped.startTime,
    endTime: mapped.endTime,
    createdAt: nowIso,
    createdBy,
    creatorName,
    status: "upcoming",
    aiLed: true,
    participantIds: [],
    agenda,
    agendaGeneratedAt: nowIso,
    recurrence: mapped.recurrence,
    maxParticipants: mapped.maxParticipants,
  });

  await ref.set(payload);
  console.log("Roundtable created", payload.id);
  return payload;
};
